import React from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';
import InputField from '../../../components/ui/InputField';
import SelectField from '../../../components/ui/SelectField';
import CustomButton from '../../../components/CustomButton';
import OrgDetailSection from './OrgDetailSection';
import { CustomerClass } from '../../../api/organization/organizationApiTypes';
// Deliberately NOT `buildCustomerClassOptions`: on a unit, `null` means
// "inherit from the organization", which is a different outcome from the
// system-default class whenever the org has a class of its own. The org-level
// list collapses those two because there it is one outcome; here they are two.
import { NO_CLASS_VALUE } from '../organizationSectionVisibility';
import { COST_CENTER_MAX_LENGTH } from '../../../utils/costCenter';
import { LEITWEG_ID_MAX_LENGTH, leitwegIdProblem } from '../../../utils/leitwegId';

/**
 * A unit row in the form. `id` is the persisted unit id when the row already
 * exists on the backend, or a synthetic `new-*` id for an unsaved row. The
 * detail page maps `new-*` rows to create-unit calls and the rest to updates.
 */
export interface OrgUnitFormRow {
    id: string;
    name: string;
    /** Unit-level class override; null = inherit the org default. */
    customerClassId: string | null;
    /** #1810: the unit's default cost centre; '' = the organisation's applies. */
    defaultCostCenter: string;
    /** The office's own Leitweg-ID; '' = the organisation's applies. */
    leitwegId: string;
}

interface OrgUnitsSectionProps {
    units: OrgUnitFormRow[];
    onChange: (units: OrgUnitFormRow[]) => void;
    classes: CustomerClass[];
    isLoading?: boolean;
    /** Render read-only (view mode): inputs disabled, no add/remove. */
    disabled?: boolean;
}

/**
 * One shallow level of organizational units under an org (plan §4.2 — no
 * nesting). Each unit may override the org's default customer class; an empty
 * list is valid (small orgs use none).
 */
const OrgUnitsSection: React.FC<OrgUnitsSectionProps> = ({
    units,
    onChange,
    classes,
    isLoading = false,
    disabled = false,
}) => {
    const { t } = useTranslation();

    // Named under the unit's field while typing; the page refuses to save
    // while any of them stands (the backend would refuse it too).
    const leitwegFieldError = (value: string) => {
        const problem = disabled ? null : leitwegIdProblem(value);
        return problem
            ? { error: true, errorText: t(`organization.validation.leitweg_id_${problem}`) }
            : {};
    };

    const activeClasses = React.useMemo(
        () => classes.filter((c) => c.archivedAt === null),
        [classes],
    );

    const classOptions = React.useMemo(
        () => [NO_CLASS_VALUE, ...activeClasses.map((c) => c.id)],
        [activeClasses],
    );

    const classLabels = React.useMemo(() => {
        const labels: Record<string, string> = {
            [NO_CLASS_VALUE]: t('organization.fields.unit_class_inherit'),
        };
        activeClasses.forEach((c) => {
            labels[c.id] = c.name;
        });
        return labels;
    }, [activeClasses, t]);

    const addUnit = () => {
        onChange([
            ...units,
            { id: `new-${Date.now()}`, name: '', customerClassId: null, defaultCostCenter: '', leitwegId: '' },
        ]);
    };

    const updateUnit = (id: string, updates: Partial<OrgUnitFormRow>) => {
        onChange(units.map((u) => (u.id === id ? { ...u, ...updates } : u)));
    };

    const removeUnit = (id: string) => {
        onChange(units.filter((u) => u.id !== id));
    };

    return (
        <OrgDetailSection
            title={t('organization.sections.units')}
            subtitle={t('organization.sections.units_subtitle')}
            help={t('organization.help.units')}
        >
            <div className={'flex flex-col gap-4'}>
                {units.length === 0 ? (
                    <p className={'text-sm text-foreground/60'}>
                        {t('organization.fields.units_empty')}
                    </p>
                ) : (
                    units.map((unit) => (
                        <div
                            key={unit.id}
                            className={'grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_auto] gap-3 items-end'}
                        >
                            <InputField
                                labelAbove={true}
                                label={t('organization.fields.unit_name')}
                                placeholder={t('organization.fields.unit_name_placeholder')}
                                value={unit.name}
                                onChange={(val) => updateUnit(unit.id, { name: val })}
                                disabled={disabled}
                            />
                            <SelectField
                                labelAbove={true}
                                label={t('organization.fields.unit_class')}
                                value={unit.customerClassId ?? NO_CLASS_VALUE}
                                onChange={(val) =>
                                    updateUnit(unit.id, {
                                        customerClassId: val === NO_CLASS_VALUE ? null : val,
                                    })
                                }
                                options={classOptions}
                                optionLabels={classLabels}
                                disabled={isLoading || disabled}
                                noOptions={t('organization.fields.unit_class_inherit')}
                            />
                            <InputField
                                labelAbove={true}
                                label={t('organization.fields.unit_cost_center')}
                                help={t('organization.help.unit_cost_center')}
                                placeholder={t('organization.fields.unit_cost_center_placeholder')}
                                value={unit.defaultCostCenter}
                                onChange={(val) => updateUnit(unit.id, { defaultCostCenter: val })}
                                maxLength={COST_CENTER_MAX_LENGTH}
                                disabled={disabled}
                            />
                            <InputField
                                labelAbove={true}
                                label={t('organization.fields.unit_leitweg_id')}
                                help={t('organization.help.unit_leitweg_id')}
                                placeholder={t('organization.fields.leitweg_id_placeholder')}
                                value={unit.leitwegId}
                                onChange={(val) => updateUnit(unit.id, { leitwegId: val })}
                                maxLength={LEITWEG_ID_MAX_LENGTH}
                                {...leitwegFieldError(unit.leitwegId)}
                                disabled={disabled}
                            />
                            {!disabled && (
                                <button
                                    type={'button'}
                                    onClick={() => removeUnit(unit.id)}
                                    aria-label={t('organization.fields.unit_remove')}
                                    className={'mb-2 p-2 text-foreground/60 hover:text-foreground transition-colors'}
                                >
                                    <Trash2 className={'h-4 w-4'} />
                                </button>
                            )}
                        </div>
                    ))
                )}
                {!disabled && (
                    <div>
                        <CustomButton
                            title={t('organization.fields.unit_add')}
                            onClick={addUnit}
                            isFilled={false}
                        />
                    </div>
                )}
            </div>
        </OrgDetailSection>
    );
};

export default OrgUnitsSection;
